import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { enablePush, disablePush, pushSupported, sendTestPush } from "@/lib/push";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { PageHeader, SliderField } from "@/components/shared";
import { PAGE_HELP } from "@/components/help";
import { BellRing, CreditCard, HandHeart, LifeBuoy, Mail, MessageSquareText, MoonStar, ShieldCheck, Smartphone } from "lucide-react";

const BLANK = { displayName: "", currentMood: "", stressLevel: 5, emotionalGoals: "", preferredRoutines: "", notes: "" };

export default function HumanProfile() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState({ notifyEmail: true, notifySms: false, notifyPush: false, phone: "", smsCarrier: "", notifyEvents: { support: true, billing: true, ritual: true, account: true }, quietHoursEnabled: false, quietStart: "22:00", quietEnd: "07:00" });
  const [carriers, setCarriers] = useState([]);
  const [prefsBusy, setPrefsBusy] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (user) setPrefs({
      notifyEmail: user.notifyEmail !== false,
      notifySms: !!user.notifySms,
      notifyPush: !!user.notifyPush,
      phone: user.phone || "",
      smsCarrier: user.smsCarrier || "",
      notifyEvents: { support: true, billing: true, ritual: true, account: true, ...(user.notifyEvents || {}) },
      quietHoursEnabled: !!user.quietHoursEnabled,
      quietStart: user.quietStart || "22:00",
      quietEnd: user.quietEnd || "07:00",
    });
  }, [user]);

  useEffect(() => {
    api.get("/push/carriers").then(({ data }) => setCarriers(data.carriers || [])).catch((e) => console.error("Failed to load carrier list:", e?.message));
  }, []);

  const togglePush = async (on) => {
    setPushBusy(true);
    try {
      if (on) {
        await enablePush(); // browser permission + device registration
        const { data } = await api.patch("/auth/me/preferences", { notifyPush: true });
        setUser(data);
        toast.success("Push notifications enabled on this device.", {
          action: { label: "Send test", onClick: () => sendTestPush().then((n) => toast.info(n ? "Test notification sent." : "No devices registered yet.")) },
        });
      } else {
        await disablePush();
        const { data } = await api.patch("/auth/me/preferences", { notifyPush: false });
        setUser(data);
        toast.success("Push notifications turned off.");
      }
    } catch (e) {
      toast.error(e?.message || errMsg(e));
      setPrefs((p) => ({ ...p, notifyPush: !on }));
    } finally { setPushBusy(false); }
  };

  const savePrefs = async () => {
    if (prefs.notifySms && !prefs.phone.trim()) { toast.error("Add a phone number to receive text notifications."); return; }
    if (prefs.notifySms && !prefs.smsCarrier) { toast.error("Choose your mobile carrier to receive text notifications."); return; }
    setPrefsBusy(true);
    try {
      const { notifyPush, ...rest } = prefs; // push is managed by its own toggle flow
      // Capture the browser's timezone so quiet hours track the user's local clock
      const { data } = await api.patch("/auth/me/preferences", { ...rest, tzOffsetMinutes: new Date().getTimezoneOffset() });
      setUser(data);
      toast.success("Notification preferences saved.");
    } catch (e) { toast.error(errMsg(e)); } finally { setPrefsBusy(false); }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/human-profiles?limit=1");
        if (data[0]) {
          setProfile(data[0]);
          setForm({
            displayName: data[0].displayName || "",
            currentMood: data[0].currentMood || "",
            stressLevel: data[0].stressLevel ?? 5,
            emotionalGoals: (data[0].emotionalGoals || []).join(", "),
            preferredRoutines: (data[0].preferredRoutines || []).join(", "),
            notes: data[0].notes || "",
          });
        }
      } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
    };
    load();
  }, []);

  const save = async () => {
    if (!form.displayName) { toast.error("Display name is required."); return; }
    setBusy(true);
    const payload = {
      displayName: form.displayName, currentMood: form.currentMood, stressLevel: form.stressLevel,
      emotionalGoals: form.emotionalGoals.split(",").map((s) => s.trim()).filter(Boolean),
      preferredRoutines: form.preferredRoutines.split(",").map((s) => s.trim()).filter(Boolean),
      notes: form.notes,
    };
    try {
      if (profile) { await api.patch(`/human-profiles/${profile.id}`, payload); }
      else { const { data } = await api.post("/human-profiles", payload); setProfile(data); }
      toast.success("Your profile is saved.");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div className="max-w-2xl">
      <PageHeader title="My Human Profile" help={PAGE_HELP.profile} subtitle="Soula Paws tracks both sides of the bond — your emotional context matters too." />
      <Card className="rounded-2xl">
        <CardContent className="p-6 space-y-4">
          <div className="space-y-1.5"><Label>Display name *</Label><Input value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} data-testid="human-name-input" /></div>
          <div className="space-y-1.5"><Label>Current mood</Label><Input placeholder="hopeful, tired, grounded..." value={form.currentMood} onChange={(e) => setForm({ ...form, currentMood: e.target.value })} data-testid="human-mood-input" /></div>
          <SliderField label="Baseline stress level" value={form.stressLevel} onChange={(v) => setForm({ ...form, stressLevel: v })} testId="human-stress-slider" />
          <div className="space-y-1.5">
            <Label>Emotional goals (comma-separated)</Label>
            <Input placeholder="reduce anxiety together, build trust with my rescue" value={form.emotionalGoals} onChange={(e) => setForm({ ...form, emotionalGoals: e.target.value })} data-testid="human-goals-input" />
            <p className="text-xs text-muted-foreground">e.g. "I want to reduce anxiety with my dog", "track grief and senior-pet support"</p>
          </div>
          <div className="space-y-1.5"><Label>Preferred routines (comma-separated)</Label><Input placeholder="morning walk, evening decompression" value={form.preferredRoutines} onChange={(e) => setForm({ ...form, preferredRoutines: e.target.value })} data-testid="human-routines-input" /></div>
          <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="human-notes-input" /></div>
          <Button onClick={save} disabled={busy} className="rounded-xl" data-testid="human-save-button">{busy ? "Saving..." : "Save profile"}</Button>
        </CardContent>
      </Card>

      {/* Notification preferences: channels + per-event controls */}
      <Card className="rounded-2xl mt-6" data-testid="notification-prefs-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><BellRing className="w-4 h-4 text-primary" /> Notifications</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">In-app alerts are always on. Choose how else we reach you, and which alerts are worth a ping.</p>

          {/* Channels */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <Mail className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div><p className="text-sm font-medium">Email notifications</p><p className="text-xs text-muted-foreground">Delivered to {user?.email}</p></div>
            </div>
            <Switch checked={prefs.notifyEmail} onCheckedChange={(v) => setPrefs((p) => ({ ...p, notifyEmail: v }))} data-testid="notify-email-switch" />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <Smartphone className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Push notifications <span className="text-[10px] font-normal text-muted-foreground">(free · this device)</span></p>
                <p className="text-xs text-muted-foreground">{pushSupported() ? "Native banner alerts with the paw icon - works great with the installed app." : "Install the app first (iPhone: Share → Add to Home Screen), then enable here."}</p>
              </div>
            </div>
            <Switch checked={prefs.notifyPush} disabled={pushBusy || !pushSupported()} onCheckedChange={(v) => { setPrefs((p) => ({ ...p, notifyPush: v })); togglePush(v); }} data-testid="notify-push-switch" />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <MessageSquareText className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div><p className="text-sm font-medium">Text messages <span className="text-[10px] font-normal text-muted-foreground">(free · via your carrier)</span></p><p className="text-xs text-muted-foreground">Sent through your carrier's free text gateway - US carriers, best-effort delivery.</p></div>
            </div>
            <Switch checked={prefs.notifySms} onCheckedChange={(v) => setPrefs((p) => ({ ...p, notifySms: v }))} data-testid="notify-sms-switch" />
          </div>
          {prefs.notifySms && (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Phone number</Label><Input type="tel" placeholder="+1 (555) 123-4567" value={prefs.phone} onChange={(e) => setPrefs((p) => ({ ...p, phone: e.target.value }))} data-testid="notify-phone-input" /></div>
              <div className="space-y-1.5">
                <Label>Mobile carrier</Label>
                <Select value={prefs.smsCarrier} onValueChange={(v) => setPrefs((p) => ({ ...p, smsCarrier: v }))}>
                  <SelectTrigger data-testid="notify-carrier-select"><SelectValue placeholder="Choose carrier" /></SelectTrigger>
                  <SelectContent>{carriers.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Per-event controls */}
          <div className="pt-2 border-t border-border">
            <p className="text-sm font-medium mb-2">Which alerts should reach you?</p>
            <div className="space-y-3">
              {[
                { key: "support", icon: LifeBuoy, label: "Support replies", hint: "When our team answers your tickets" },
                { key: "billing", icon: CreditCard, label: "Billing & subscription", hint: "Plan changes, trials, credits" },
                { key: "ritual", icon: HandHeart, label: "Daily ritual reminders", hint: "When today's ritual & enrichment ideas are ready" },
                { key: "account", icon: ShieldCheck, label: "Account & security", hint: "Important account activity" },
              ].map((ev) => (
                <div key={ev.key} className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2.5">
                    <ev.icon className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div><p className="text-sm">{ev.label}</p><p className="text-xs text-muted-foreground">{ev.hint}</p></div>
                  </div>
                  <Switch checked={prefs.notifyEvents[ev.key] !== false} onCheckedChange={(v) => setPrefs((p) => ({ ...p, notifyEvents: { ...p.notifyEvents, [ev.key]: v } }))} data-testid={`notify-event-${ev.key}-switch`} />
                </div>
              ))}
            </div>
          </div>

          {/* Quiet hours */}
          <div className="pt-2 border-t border-border space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <MoonStar className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium">Quiet hours</p>
                  <p className="text-xs text-muted-foreground">Pause push & text pings overnight — emails still arrive silently, and everything waits in your in-app inbox.</p>
                </div>
              </div>
              <Switch checked={prefs.quietHoursEnabled} onCheckedChange={(v) => setPrefs((p) => ({ ...p, quietHoursEnabled: v }))} data-testid="quiet-hours-switch" />
            </div>
            {prefs.quietHoursEnabled && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>From</Label><Input type="time" value={prefs.quietStart} onChange={(e) => setPrefs((p) => ({ ...p, quietStart: e.target.value }))} data-testid="quiet-start-input" /></div>
                <div className="space-y-1.5"><Label>Until</Label><Input type="time" value={prefs.quietEnd} onChange={(e) => setPrefs((p) => ({ ...p, quietEnd: e.target.value }))} data-testid="quiet-end-input" /></div>
              </div>
            )}
          </div>

          <Button onClick={savePrefs} disabled={prefsBusy} variant="secondary" className="rounded-xl" data-testid="notify-prefs-save-button">{prefsBusy ? "Saving..." : "Save notification preferences"}</Button>
        </CardContent>
      </Card>
    </div>
  );
}
